import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { Check, X, ArrowRight, ShieldCheck, DollarSign, Clock, Users, Sparkles } from "lucide-react";
import { Reveal } from "./Reveal";
import { CALENDLY_URL } from "@/lib/site";
import { trackScheduleConsultation } from "@/lib/analytics";

export function FullTimeVsFractional() {
  const [txVolume, setTxVolume] = useState<"low" | "medium" | "high">("low");

  const savingsData = {
    low: {
      txns: "1–75 transactions/mo",
      recommendedPlan: "Essential (from $199/mo)",
      planPrice: 199,
      inHouseCost: 6500,
      annualSavings: 75612,
      bestFor: "Solo founders, consultants, LLCs, micro-agencies & pre-seed startups",
      hoursNeeded: "5–10 hrs/mo",
    },
    medium: {
      txns: "75–250 transactions/mo",
      recommendedPlan: "Growth (from $499/mo)",
      planPrice: 499,
      inHouseCost: 6500,
      annualSavings: 72012,
      bestFor: "Growing SMBs, agencies with payroll, active eCommerce stores",
      hoursNeeded: "15–30 hrs/mo",
    },
    high: {
      txns: "250+ transactions/mo",
      recommendedPlan: "Scale (from $1,299/mo)",
      planPrice: 1299,
      inHouseCost: 7500,
      annualSavings: 74412,
      bestFor: "Venture-backed startups, multi-state businesses, complex revenue",
      hoursNeeded: "40–60 hrs/mo",
    },
  };

  const current = savingsData[txVolume];

  return (
    <section className="relative overflow-hidden bg-brand-surface/60 py-24 px-6 border-y border-brand-navy/5">
      <div className="mx-auto max-w-7xl">
        <Reveal className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center gap-2 rounded-full bg-brand-accent/15 px-3.5 py-1.5 text-xs font-extrabold uppercase tracking-wider text-brand-navy">
            <Sparkles className="size-3.5 text-brand-accent" />
            The Fractional Advantage
          </div>
          <h2 className="mt-4 font-display text-3xl font-extrabold tracking-tight text-brand-navy sm:text-5xl">
            Do you really need a full-time bookkeeper?
          </h2>
          <p className="mt-4 text-lg leading-relaxed text-brand-navy/70">
            In the US, hiring an in-house bookkeeper costs over <strong className="text-brand-navy font-bold">$78,000/year</strong> once you add salary, payroll taxes, and benefits. For businesses with under 100 transactions a month, you are paying for 150 hours of downtime.
          </p>
        </Reveal>

        {/* Volume selector tabs */}
        <Reveal delay={0.1} className="mb-12">
          <div className="mx-auto max-w-xl flex items-center justify-center p-1.5 rounded-2xl bg-brand-navy/5 border border-brand-navy/10 backdrop-blur-xs">
            <button
              type="button"
              onClick={() => setTxVolume("low")}
              className={`flex-1 py-2.5 px-3 rounded-xl text-xs sm:text-sm font-bold transition-all ${
                txVolume === "low"
                  ? "bg-brand-navy text-background shadow-md"
                  : "text-brand-navy/70 hover:text-brand-navy"
              }`}
            >
              Up to 75 Txns (No Full-Time Needed)
            </button>
            <button
              type="button"
              onClick={() => setTxVolume("medium")}
              className={`flex-1 py-2.5 px-3 rounded-xl text-xs sm:text-sm font-bold transition-all ${
                txVolume === "medium"
                  ? "bg-brand-navy text-background shadow-md"
                  : "text-brand-navy/70 hover:text-brand-navy"
              }`}
            >
              75–250 Txns (Growing)
            </button>
            <button
              type="button"
              onClick={() => setTxVolume("high")}
              className={`flex-1 py-2.5 px-3 rounded-xl text-xs sm:text-sm font-bold transition-all ${
                txVolume === "high"
                  ? "bg-brand-navy text-background shadow-md"
                  : "text-brand-navy/70 hover:text-brand-navy"
              }`}
            >
              250+ Txns (Scale)
            </button>
          </div>
        </Reveal>

        {/* Side-by-Side Comparison Grid */}
        <div className="grid gap-8 lg:grid-cols-2 max-w-5xl mx-auto items-stretch">
          {/* Card 1: In-House Full-Time Bookkeeper */}
          <Reveal delay={0.15} className="h-full">
            <div className="h-full rounded-3xl border border-brand-navy/10 bg-background/80 backdrop-blur-md p-8 sm:p-10 flex flex-col justify-between shadow-xs">
              <div>
                <div className="flex items-center justify-between pb-6 border-b border-brand-navy/10">
                  <div>
                    <span className="text-xs font-bold uppercase tracking-wider text-rose-600 bg-rose-50 px-2.5 py-1 rounded-md">
                      Traditional In-House Route
                    </span>
                    <h3 className="mt-2 text-2xl font-bold text-brand-navy">
                      Full-Time In-House Bookkeeper
                    </h3>
                  </div>
                  <div className="text-right">
                    <span className="text-xs font-semibold text-brand-navy/50 block">Est. Cost</span>
                    <div className="font-display text-3xl font-extrabold text-brand-navy">
                      ${current.inHouseCost.toLocaleString()}
                      <span className="text-sm font-normal text-brand-navy/60">/mo</span>
                    </div>
                  </div>
                </div>

                <div className="mt-6 space-y-4 text-sm text-brand-navy/75">
                  <div className="flex items-start gap-3">
                    <X className="size-4 shrink-0 text-rose-500 mt-1" strokeWidth={2.5} />
                    <div>
                      <strong className="text-brand-navy font-semibold">Expensive overhead:</strong> Average US salary ($65,000) + employer payroll taxes (7.65%) + healthcare & benefits = <span className="font-semibold text-rose-600">~$78,000–$85,000/year</span>.
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <X className="size-4 shrink-0 text-rose-500 mt-1" strokeWidth={2.5} />
                    <div>
                      <strong className="text-brand-navy font-semibold">Paying for idle time:</strong> You pay for 160 hours/month even if your business only requires {current.hoursNeeded} of active bookkeeping.
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <X className="size-4 shrink-0 text-rose-500 mt-1" strokeWidth={2.5} />
                    <div>
                      <strong className="text-brand-navy font-semibold">Single point of failure:</strong> Sick leaves, vacation delays, employee turnover, and expensive training cycles.
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <X className="size-4 shrink-0 text-rose-500 mt-1" strokeWidth={2.5} />
                    <div>
                      <strong className="text-brand-navy font-semibold">No controller review:</strong> A standalone junior bookkeeper still makes mistakes that require expensive CPA cleanup at tax time.
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-8 pt-6 border-t border-brand-navy/10 text-xs font-medium text-brand-navy/50">
                Annual financial commitment: ~${(current.inHouseCost * 12).toLocaleString()}/year + recruiting fees
              </div>
            </div>
          </Reveal>

          {/* Card 2: Finendra Fractional Bookkeeper */}
          <Reveal delay={0.2} className="h-full">
            <div className="relative h-full rounded-3xl border-2 border-brand-accent bg-background p-8 sm:p-10 flex flex-col justify-between shadow-2xl ring-4 ring-brand-accent/10">
              {/* Highlight ribbon */}
              <div className="absolute -top-3.5 right-8 bg-brand-accent text-brand-navy text-[11px] font-extrabold uppercase tracking-wider py-1 px-3.5 rounded-full shadow-md flex items-center gap-1">
                <Sparkles className="size-3" /> Recommended Solution
              </div>

              <div>
                <div className="flex items-center justify-between pb-6 border-b border-brand-navy/10">
                  <div>
                    <span className="text-xs font-bold uppercase tracking-wider text-brand-accent bg-brand-accent/10 px-2.5 py-1 rounded-md">
                      Finendra Fractional Service
                    </span>
                    <h3 className="mt-2 text-2xl font-bold text-brand-navy">
                      Dedicated Fractional Bookkeeper
                    </h3>
                  </div>
                  <div className="text-right">
                    <span className="text-xs font-semibold text-brand-navy/50 block">Transparent Fee</span>
                    <div className="font-display text-4xl font-extrabold text-brand-navy">
                      <span className="text-xs font-bold uppercase tracking-wider text-brand-navy/60 mr-1.5 align-middle">
                        From
                      </span>
                      ${current.planPrice}
                      <span className="text-sm font-normal text-brand-navy/60">/mo</span>
                    </div>
                  </div>
                </div>

                {/* Savings callout pill */}
                <div className="mt-6 rounded-2xl bg-emerald-500/10 border border-emerald-500/20 p-4 flex items-center gap-3">
                  <div className="size-9 rounded-xl bg-emerald-500/20 text-emerald-700 flex items-center justify-center shrink-0 font-bold">
                    $
                  </div>
                  <div>
                    <div className="text-xs font-bold uppercase text-emerald-800 tracking-wider">
                      Annual Business Savings
                    </div>
                    <div className="text-lg font-extrabold text-emerald-700">
                      Save ${current.annualSavings.toLocaleString()} / year ({(100 - (current.planPrice / current.inHouseCost) * 100).toFixed(0)}% less)
                    </div>
                  </div>
                </div>

                <div className="mt-6 space-y-4 text-sm text-brand-navy/85">
                  <div className="flex items-start gap-3">
                    <Check className="size-4 shrink-0 text-brand-accent mt-1" strokeWidth={3} />
                    <div>
                      <strong className="text-brand-navy font-semibold">Tailored for your volume:</strong> {current.txns} reconciled monthly in QuickBooks Online or Xero with zero wasted spend.
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Check className="size-4 shrink-0 text-brand-accent mt-1" strokeWidth={3} />
                    <div>
                      <strong className="text-brand-navy font-semibold">Dual-layer quality:</strong> Dedicated certified bookkeeper + senior Controller review on every month-end reconciliation.
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Check className="size-4 shrink-0 text-brand-accent mt-1" strokeWidth={3} />
                    <div>
                      <strong className="text-brand-navy font-semibold">CPA tax-ready:</strong> Monthly P&L, Balance Sheet, 1099 vendor tracking, and clean workpapers ready for tax season.
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Check className="size-4 shrink-0 text-brand-accent mt-1" strokeWidth={3} />
                    <div>
                      <strong className="text-brand-navy font-semibold">100% Flexible:</strong> Month-to-month retainer, zero lock-in, live within 48 hours without hiring headaches.
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-8 pt-6 border-t border-brand-navy/10 flex flex-col sm:flex-row items-center gap-3">
                <Link
                  to="/contact"
                  search={{ plan: current.recommendedPlan.toLowerCase().includes("essential") ? "essential" : "growth" }}
                  className="w-full sm:w-auto flex-1 rounded-xl bg-brand-navy px-6 py-3.5 text-center text-sm font-bold text-background transition-all hover:bg-brand-accent hover:shadow-lg active:scale-[0.98]"
                >
                  Start with {current.recommendedPlan}
                </Link>
                <a
                  href={CALENDLY_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackScheduleConsultation("vs_fulltime_section")}
                  className="w-full sm:w-auto rounded-xl border border-brand-navy/15 px-5 py-3.5 text-center text-sm font-bold text-brand-navy transition-all hover:bg-brand-surface active:scale-[0.98]"
                >
                  Book 15-Min Scoping Call
                </a>
              </div>
            </div>
          </Reveal>
        </div>

        {/* Small trust indicator under cards */}
        <Reveal delay={0.25} className="mt-12 text-center">
          <p className="text-xs text-brand-navy/60 font-medium">
            🔒 Month-to-month retainers • QuickBooks & Xero Certified ProAdvisor • US Standard Chart of Accounts • No setup fees
          </p>
        </Reveal>
      </div>
    </section>
  );
}
